import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";

const COOKIE_KEY = "cookie_consent";

type ConsentValue = "accepted" | "declined";

function getConsent(): ConsentValue | null {
  try {
    const val = localStorage.getItem(COOKIE_KEY);
    if (val === "accepted" || val === "declined") return val;
  } catch {}
  return null;
}

function setConsent(value: ConsentValue) {
  try {
    localStorage.setItem(COOKIE_KEY, value);
  } catch {}
}

/** Load GA4 and Clarity scripts dynamically */
export function loadAnalytics() {
  if (typeof window === "undefined") return;
  // Avoid double-loading
  if (document.getElementById("ga4-script")) return;

  // GA4
  const gaScript = document.createElement("script");
  gaScript.id = "ga4-script";
  gaScript.async = true;
  gaScript.src = "https://www.googletagmanager.com/gtag/js?id=G-DPMHLRC9HM";
  document.head.appendChild(gaScript);

  const gaInline = document.createElement("script");
  gaInline.id = "ga4-config";
  gaInline.textContent = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', 'G-DPMHLRC9HM');
  `;
  document.head.appendChild(gaInline);

  // Microsoft Clarity
  const clarityScript = document.createElement("script");
  clarityScript.id = "clarity-script";
  clarityScript.textContent = `
    (function(c,l,a,r,i,t,y){
      c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
      t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
      y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
    })(window, document, "clarity", "script", "vl3ek5y761");
  `;
  document.head.appendChild(clarityScript);
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const consent = getConsent();
    if (consent === "accepted") {
      loadAnalytics();
    } else if (consent === null) {
      // No choice made yet — show banner after short delay
      const timer = setTimeout(() => setVisible(true), 1000);
      return () => clearTimeout(timer);
    }
    // If declined, do nothing
  }, []);

  function handleAccept() {
    setConsent("accepted");
    loadAnalytics();
    setVisible(false);
  }

  function handleDecline() {
    setConsent("declined");
    setVisible(false);
  }

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 border-t border-gray-200 bg-white shadow-lg animate-in slide-in-from-bottom duration-300">
      <div className="container mx-auto px-4 lg:px-8 py-3 sm:py-3 py-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <Cookie className="w-4 h-4 text-emerald-600 shrink-0 hidden sm:block" />
          <p className="text-sm text-gray-600 leading-relaxed sm:flex-1">
            We use cookies to improve your experience. No data is sold or
            shared.{" "}
            <Link to="/privacy" className="text-emerald-600 hover:underline">
              Privacy Policy
            </Link>
          </p>
          <div className="flex items-center gap-2.5 shrink-0 w-full sm:w-auto">
            <button
              onClick={handleDecline}
              className="flex-1 sm:flex-none px-4 py-2 sm:py-1.5 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
            >
              Opt Out
            </button>
            <button
              onClick={handleAccept}
              className="flex-1 sm:flex-none px-4 py-2 sm:py-1.5 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 transition-colors"
            >
              Accept
            </button>
            <button
              onClick={handleDecline}
              className="text-gray-400 hover:text-gray-600 transition-colors ml-1"
              aria-label="Close cookie banner"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
